'use client';

import AppImage from '@/components/ui/AppImage';

const AboutContent = () => {
  return (
    <div className="relative bg-white">
      {/* Elegant Hero Section with Professional Photo */}
      <section className="relative overflow-hidden">
        {/* White background to match sections below */}
        <div className="absolute inset-0 bg-white"></div>
        
        {/* Subtle overlay for sophistication */}
        <div className="absolute inset-0 bg-white/20"></div>
        
        {/* Sophisticated background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #A47C48 1px, transparent 0)`,
            backgroundSize: '48px 48px'
          }}></div>
        </div>
        
        {/* Decorative geometric elements */}
        <div className="absolute top-0 right-0 w-96 h-96 border border-[#A47C48]/20 rounded-full translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 border border-[#A47C48]/20 rounded-full -translate-x-1/2 translate-y-1/2"></div>

        <div className="relative max-w-[1600px] mx-auto px-8 lg:px-20 py-12 lg:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            
            {/* Left: Professional Photo with Premium Frame */}
            <div className="relative lg:pl-12">
              
              <div className="relative">
                <div className="relative aspect-[3/4] max-w-md mx-auto overflow-hidden shadow-2xl">
                  <AppImage
                    src="/assets/images/Screenshot_20251214_151745_Gallery-1768826710153.jpg"
                    alt="Professional portrait of Nadia Dridi, Founding Partner at ND Law"
                    className="w-full h-full object-cover" />

                  {/* Sophisticated overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0E3B3C]/40 via-transparent to-transparent"></div>
                </div>
              </div>
            </div>

            {/* Right: Name & Title with Elegant Typography */}
            <div className="text-[#0E3B3C] lg:pr-12">
              <div className="space-y-6">
                <div>
                  <h1
                    className="text-5xl lg:text-6xl mb-4 tracking-tight leading-none text-[#0E3B3C] font-semibold"
                    style={{ fontFamily: '"Cormorant Garamond", serif' }}>

                    Nadia Dridi
                  </h1>
                  <p className="text-2xl lg:text-3xl text-[#A47C48] font-light tracking-wider mb-6">Founding Partner

                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Professional Background Section with Enhanced Typography */}
      <section className="relative bg-white py-24 lg:py-32">
        {/* Remove subtle background texture - use clean white background */}

        <div className="relative max-w-[1400px] mx-auto px-8 lg:px-20">
          {/* Elegant section header */}
          <div className="flex items-center gap-6 mb-16">
            <div className="flex items-center gap-3">
              
              <div className="w-24 h-[1px] bg-[#0E3B3C]"></div>
            </div>
            <h2 className="text-sm uppercase tracking-[0.3em] text-[#0E3B3C]/60 font-semibold">
              Professional Profile
            </h2>
          </div>

          <div className="space-y-8 max-w-5xl">
            <p className="text-[#0E3B3C] text-xl leading-[1.9] text-justify font-light first-letter:text-6xl first-letter:font-bold first-letter:text-[#0E3B3C] first-letter:mr-3 first-letter:float-left first-letter:leading-[0.9]" style={{ fontFamily: '"Crimson Text", serif' }}>
              Nadia Dridi is a lawyer registered at the Paris Bar, specialised in international commercial and investment arbitration, with experience acquired within leading international law firms in Paris and Asia, and through sustained involvement in complex, high-value disputes at the highest level of international practice.
            </p>
            
            <p className="text-[#0E3B3C] text-xl leading-[1.9] text-justify font-light" style={{ fontFamily: '"Crimson Text", serif' }}>
              Her practice includes representation in major commercial and investment arbitration proceedings conducted under the arbitration rules of leading institutions, including the ICC, ICSID, LCIA and SIAC, as well as under the UNCITRAL Arbitration Rules. She has also acted in treaty-based arbitrations initiated under the investment agreement of the Organisation of Islamic Cooperation (OIC).
            </p>
            
            <p className="text-[#0E3B3C] text-xl leading-[1.9] text-justify font-light" style={{ fontFamily: '"Crimson Text", serif' }}>
              She has advised and representated States, State-owned entities, multinational corporations and private actors across Europe, the MENA region, Africa and Asia, and have required legal analysis closely intertwined with political sensitivity, institutional constraints and strategic considerations.
            </p>
            
            <p className="text-[#0E3B3C] text-xl leading-[1.9] text-justify font-light" style={{ fontFamily: '"Crimson Text", serif' }}>
              Her practice is grounded in a rigorous command of public and private international law, combined with a deep understanding of how disputes unfold in practice within governmental, corporate and cross-border environments.
            </p>
            
            <p className="text-[#0E3B3C] text-xl leading-[1.9] text-justify font-light" style={{ fontFamily: '"Crimson Text", serif' }}>
              She has extensive experience across sectors that routinely generate complex international disputes, including construction and large-scale infrastructure projects, telecommunications, and energy, including oil and gas.
            </p>
            
            <div className="pt-8 pl-8 border-l-2 border-[#A47C48]">
              <p className="text-[#0E3B3C] text-xl leading-[1.9] text-justify font-light italic" style={{ fontFamily: '"Crimson Text", serif' }}>
                What distinguishes her approach is her ability to operate at the intersection of law, strategy and context. She brings to each matter a capacity to translate a client's institutional, commercial and cultural realities into effective arbitral advocacy, ensuring that these dimensions are not only understood, but meaningfully integrated into the conduct of the dispute.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Language Capabilities Section with Premium Cards */}
      <section className="relative bg-white py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-20">
          {/* Elegant section header */}
          <div className="flex items-center justify-between mb-16">
            <div>
              <div className="flex items-center gap-6 mb-4">
                <div className="flex items-center gap-3">
                  
                  <div className="w-24 h-[1px] bg-[#0E3B3C]"></div>
                </div>
                <h2 className="text-sm uppercase tracking-[0.3em] text-[#0E3B3C]/60 font-semibold">
                  Multilingual Representation
                </h2>
              </div>
              <h3
                className="text-3xl lg:text-4xl font-bold text-[#0E3B3C] tracking-tight"
                style={{ fontFamily: '"Playfair Display", serif' }}>

                Language Capabilities
              </h3>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="group relative bg-gradient-to-br from-[#F4F1EA] to-white p-10 transition-all duration-500 hover:shadow-2xl border border-[#0E3B3C]/5">
              <div className="relative">
                <div className="mb-6">
                  <h3 className="text-2xl font-semibold text-[#0E3B3C] mb-2 tracking-tight" style={{ fontFamily: '"Playfair Display", serif' }}>
                    Professional fluency
                  </h3>
                </div>
                <p className="text-[#0E3B3C] text-lg leading-relaxed font-light">
                  French · English · Arabic
                </p>
              </div>
            </div>
            
            <div className="group relative bg-gradient-to-br from-[#F4F1EA] to-white p-10 transition-all duration-500 hover:shadow-2xl border border-[#0E3B3C]/5">
              <div className="relative">
                <div className="mb-6">
                  <h3 className="text-2xl font-semibold text-[#0E3B3C] mb-2 tracking-tight" style={{ fontFamily: '"Playfair Display", serif' }}>
                    Working proficiency
                  </h3>
                </div>
                <p className="text-[#0E3B3C] text-lg leading-relaxed font-light">
                  Spanish
                </p>
              </div>
            </div>
            
            <div className="group relative bg-gradient-to-br from-[#F4F1EA] to-white p-10 transition-all duration-500 hover:shadow-2xl border border-[#0E3B3C]/5">
              <div className="relative">
                <div className="mb-6">
                  <h3 className="text-2xl font-semibold text-[#0E3B3C] mb-2 tracking-tight" style={{ fontFamily: '"Playfair Display", serif' }}>
                    Understanding
                  </h3>
                </div>
                <p className="text-[#0E3B3C] text-lg leading-relaxed font-light">
                  Khmer
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sectoral Expertise Section with Sophisticated Design */}
      <section className="relative bg-white py-24 lg:py-32 overflow-hidden">
        {/* Elegant background pattern */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #A47C48 1px, transparent 0)`,
            backgroundSize: '48px 48px'
          }}></div>
        </div>

        <div className="relative max-w-[1400px] mx-auto px-8 lg:px-20">
          {/* Premium section header */}
          <div className="mb-16">
            <div className="flex items-center gap-6 mb-4">
              <div className="flex items-center gap-3">
                
                <div className="w-24 h-[1px] bg-[#0E3B3C]"></div>
              </div>
              <h2 className="text-sm uppercase tracking-[0.3em] text-[#0E3B3C]/60 font-semibold">
                Practice Areas
              </h2>
            </div>
            <h3
              className="text-3xl lg:text-4xl font-bold text-[#0E3B3C] mb-6 tracking-tight"
              style={{ fontFamily: '"Playfair Display", serif' }}>

              Sectoral Expertise
            </h3>
            <p className="text-[#0E3B3C]/70 text-xl leading-relaxed font-light whitespace-nowrap">
              Extensive experience across sectors that routinely generate complex international disputes
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Construction & Infrastructure */}
            <div className="group relative bg-gradient-to-br from-[#F4F1EA] to-white p-6 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl border border-[#0E3B3C]/5">
              
              <div className="mb-4">
                
                <h3 className="text-3xl font-bold text-[#0E3B3C] mb-4 tracking-tight" style={{ fontFamily: '"Playfair Display", serif' }}>
                  Construction &<br />Infrastructure
                </h3>
              </div>
              
              <p className="text-[#0E3B3C]/80 text-base leading-relaxed font-light text-justify">
                Large-scale infrastructure projects and complex construction disputes
              </p>
            </div>

            {/* Technology */}
            <div className="group relative bg-gradient-to-br from-[#F4F1EA] to-white p-6 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl border border-[#0E3B3C]/5">
              
              <div className="mb-4">
                
                <h3 className="text-3xl font-bold text-[#0E3B3C] mb-4 tracking-tight" style={{ fontFamily: '"Playfair Display", serif' }}>
                  Technology
                </h3>
              </div>
              
              <p className="text-[#0E3B3C]/80 text-base leading-relaxed font-light text-justify">
                Arbitration in technology and telecommunications sectors
              </p>
            </div>

            {/* Energy and Natural Resources */}
            <div className="group relative bg-gradient-to-br from-[#F4F1EA] to-white p-6 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl border border-[#0E3B3C]/5">
              
              <div className="mb-4">
                
                <h3 className="text-3xl font-bold text-[#0E3B3C] mb-4 tracking-tight" style={{ fontFamily: '"Playfair Display", serif' }}>
                  Energy and<br />Natural Resources
                </h3>
              </div>
              
              <p className="text-[#0E3B3C]/80 text-base leading-relaxed font-light text-justify">
                Disputes across the energy and natural resources value chain, including oil and gas, mining, metals, renewable energy, and energy-related infrastructure
              </p>
            </div>
          </div>
        </div>
      </section>

    </div>);

};

export default AboutContent;